import type { CenterProps, FlexProps, MotionPropsFor } from '@hover/blueprint';
import {
  Box,
  Flex,
  HStack,
  Heading,
  calc,
  cssVar,
  cssVariables,
  forwardRef,
  motion,
  system,
  useTheme,
} from '@hover/blueprint';
import { iChevronDown } from '@hover/icons';
import {
  AnimatePresence,
  MotionConfig,
  useScroll,
  useTransform,
} from 'framer-motion';
import React, { useRef } from 'react';

import { Button } from 'src/shared/components';
import { Web } from 'src/shared/constants/FeatureFlag';
import { useTreatment } from 'src/shared/hooks';
import { useIsScrollable } from 'src/shared/hooks/scrollHooks/useIsScrollable';
import { useMaxWidthQuery } from 'src/shared/hooks/scrollHooks/useMaxWidthQuery';
import { buttonPressed } from 'src/shared/telemetry';
import { isEnabled } from 'src/shared/telemetry/featureFlag';
import { presenceVariants } from 'src/shared/utils/motion';
import { hideScrollbarsStyles } from 'src/shared/utils/styles';

import { useHomeownerAppContext, useHomeownerTranslate } from '../../hooks';
import { CobrandLogo } from '../CobrandLogo/CobrandLogo';

import { getCategoryHeading } from './menu';
import { useCategoryCobranding } from './partners';
import { useCustomization } from './useCustomization';
import { useImageTracking } from './useImageTracking';

const MotionFlex = motion(Flex);
const MotionHStack = motion(HStack);

interface SelectPaletteButtonProps extends MotionPropsFor<CenterProps> {
  label: string;
}

const $overlayBleed = cssVariables.space(1000);

const Overlay = (props: FlexProps) => (
  <Flex
    alignItems="center"
    bg="neutral.0"
    boxSizing="border-box"
    display={{ base: 'none', tablet: 'flex' }}
    flexShrink={0}
    gap="300"
    mx={calc.multiply(-1, $overlayBleed)}
    position="sticky"
    px={$overlayBleed.reference}
    width={calc.add('100%', calc.multiply($overlayBleed, 2))}
    zIndex={3}
    {...props}
  />
);

const $topShadowOpacity = cssVar('top-shadow-opacity');
const $bottomShadowOpacity = cssVar('bottom-shadow-opacity');

const useShadow = (position: 'top' | 'bottom', length: string = '25px') => {
  const to = position === 'top' ? 'bottom' : 'top';

  const {
    colors: { neutral },
  } = useTheme();

  return {
    background: `
      linear-gradient(to ${to}, ${neutral[900]}, transparent 25%),
      linear-gradient(to ${to}, ${neutral[900]}, transparent 40%),
      linear-gradient(to ${to}, ${neutral[900]}, transparent 100%)
    `,
    position: 'absolute',
    content: '""',
    left: 0,
    right: 0,
    [to]: calc.multiply(-1, length),
    height: length,
    opacity:
      position === 'top'
        ? $topShadowOpacity.reference
        : $bottomShadowOpacity.reference,
    pointerEvents: 'none',
    zIndex: 1,
  };
};

const buttonPresence = presenceVariants({
  animate: { opacity: 1, y: '0%' },
  exit: { opacity: 0, y: '50%' },
  initial: { opacity: 0, y: '50%' },
});

export interface CustomizeControlsProps extends MotionPropsFor<FlexProps> {
  paletteLabel: SelectPaletteButtonProps['label'];
  onSelectPaletteClick?: () => void;
  children: React.ReactNode;
}

/**
 * Responsive component for different customize controls such as paint and
 * material palettes that includes the button to open the palette selection
 * sheet.
 */
const CustomizeControls = forwardRef<CustomizeControlsProps, 'div'>(
  ({ children, paletteLabel, onSelectPaletteClick, ...props }, ref) => {
    const t = useHomeownerTranslate();
    const applyButtonText = t('customize.apply');

    const belowTablet = useMaxWidthQuery('tablet');

    const {
      category,
      customization,
      history,
      isCustomizing,
      onApply,
      onCancel,
      baseImage,
    } = useCustomization();

    const { trackingData: contextTrackingData } = useHomeownerAppContext();
    const imageTracking = useImageTracking();

    const isNewProjectUiEnabled = isEnabled(useTreatment(Web.NEW_PROJECT_UI));

    const cobranding = useCategoryCobranding();

    const scrollRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({ container: scrollRef });
    const [isScrollable] = useIsScrollable({
      ref: scrollRef,
      direction: 'vertical',
    });

    const topShadow = useShadow('top');
    const bottomShadow = useShadow('bottom');

    const shadowStrength = 0.05;
    const topShadowOpacity = useTransform(
      scrollYProgress,
      [0, 0.05],
      [0, shadowStrength],
    );
    const bottomShadowOpacity = useTransform(
      scrollYProgress,
      [0.95, 1],
      [shadowStrength, 0],
    );

    const handleApply = () => {
      buttonPressed({
        ...contextTrackingData,
        ...imageTracking,
        button_text: applyButtonText,
        primary_cta: true,
        product_name: customization?.product.primaryLabel,
        product_group: customization?.product.palettes[0],
        track_id: 'apply-edit',
      });
      onApply();
    };

    return (
      <MotionFlex
        alignItems="center"
        boxSizing="border-box"
        direction="column"
        gap={{ base: '500', tablet: '0' }}
        height="100%"
        pb={baseImage.fromSample ? '0' : { base: '600', tablet: 0 }}
        position="relative"
        pt={baseImage.fromSample ? '0' : { base: '500', tablet: 0 }}
        {...props}
        ref={ref}
        style={{
          [$topShadowOpacity.variable]: isScrollable ? topShadowOpacity : 0,
          [$bottomShadowOpacity.variable]: isScrollable
            ? bottomShadowOpacity
            : shadowStrength,
        }}
      >
        <Box
          alignItems="start"
          boxSizing="border-box"
          flexDirection={{ base: 'row', tablet: 'column' }}
          height={{ tablet: '100%' }}
          overflowX={{ base: 'auto', tablet: 'hidden' }}
          overflowY={{ base: 'hidden', tablet: 'auto' }}
          px={{ base: 0, tablet: '800', desktop: '10%' }}
          ref={scrollRef}
          sx={belowTablet(hideScrollbarsStyles())}
          width="100%"
        >
          <Overlay
            _after={topShadow}
            alignItems="start"
            color="neutral.900"
            direction="column"
            gap="500"
            justifyContent="start"
            pb="600"
            pt="700"
            top={0}
          >
            <Box
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Heading as="h1" color="inherit" noMargin size={600}>
                {getCategoryHeading(t, category)}
              </Heading>
              {isNewProjectUiEnabled && cobranding && (
                <CobrandLogo hideHoverMark />
              )}
            </Box>
            <Button
              iconAfter={iChevronDown}
              onClick={onSelectPaletteClick}
              pr="400"
              variant="secondary"
            >
              {paletteLabel}
            </Button>
          </Overlay>
          <system.div
            flex={1}
            px={{ base: '500', tablet: 'unset' }}
            width={{ base: 'auto', tablet: '100%' }}
          >
            {children}
          </system.div>
          {!baseImage.fromSample && (
            <Overlay
              _before={bottomShadow}
              bottom={0}
              direction="row"
              pb="700"
              pt="600"
            >
              <MotionConfig transition={{ duration: 0.2 }}>
                <AnimatePresence initial={false} mode="wait">
                  {history.hasChanges ? (
                    <MotionHStack
                      key="apply"
                      spacing="200"
                      width="100%"
                      {...buttonPresence}
                    >
                      <Button
                        disabled={isCustomizing}
                        onClick={handleApply}
                        px="600"
                      >
                        {applyButtonText}
                      </Button>
                      <Button onClick={onCancel} px="600" variant="secondary">
                        {t('customize.cancel')}
                      </Button>
                    </MotionHStack>
                  ) : (
                    <motion.div key="close" width="100%" {...buttonPresence}>
                      <Button onClick={onCancel} px="600">
                        {t('customize.close')}
                      </Button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </MotionConfig>
            </Overlay>
          )}
        </Box>
      </MotionFlex>
    );
  },
);

export { CustomizeControls };
